import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { otaService, OTACheckResponse } from '../services/otaService';

export const useOTAVersions = (limit = 20, offset = 0) => {
  return useQuery({
    queryKey: ['ota-versions', limit, offset],
    queryFn: () => otaService.getVersions(limit, offset),
    retry: 1,
  });
};

export const useUploadFirmware = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      version,
      changelog,
      isStable,
      isBeta,
    }: {
      file: File;
      version: string;
      changelog: string;
      isStable: boolean;
      isBeta: boolean;
    }) => otaService.uploadFirmware(file, version, changelog, isStable, isBeta),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ota-versions'] });
    },
  });
};

export const useTriggerUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, version }: { deviceId: string; version: string }) =>
      otaService.triggerUpdate(deviceId, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ota-versions'] });
    },
  });
};

/**
 * Check for beta firmware updates for a specific device.
 * Polls every 5 minutes. Used in the dashboard to show beta update notifications.
 */
export const useBetaUpdateCheck = (deviceId: string, currentVersion: string, enabled = true) => {
  return useQuery<OTACheckResponse>({
    queryKey: ['ota-beta-check', deviceId, currentVersion],
    queryFn: () => otaService.checkForBetaUpdate(deviceId, currentVersion),
    enabled: enabled && !!deviceId && !!currentVersion,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    staleTime: 4 * 60 * 1000,
  });
};
